/**
 * Write a content page — the privacy policy, terms, a returns policy — that the customer portal,
 * the public website and the apps all read. One screen for a new page (`/content/pages/new`) and an
 * existing one (`/content/pages/:slug`). Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * `QuillEditor` field under a `Controller`),
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the `Form`
 * + `FormFooter` shell every FORM screen in this app uses) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/logistics/tracking/shipping-info.tsx (the
 * `InfoBlock` facts in the Where it shows section). Beside the editor: a live preview of both public
 * pages (`components/content/page-preview.tsx`) and a guide to what each formatting choice becomes.
 *
 * The address (slug) is set once, from the title, and never changes: it is what both sites link to.
 * The API sanitises what is saved and refuses a blank page; its 400 lands on the field it names.
 */
import { useState, type ReactNode } from "react";
import { useLoaderData, useNavigate, type LoaderFunctionArgs } from "react-router-dom";
import { Controller } from "react-hook-form";
import { z } from "zod";
import toast from "react-hot-toast";
import { Button, Text } from "rizzui";
import {
  PiBroadcastDuotone as StatusIcon,
  PiCalendarCheckDuotone as DateIcon,
  PiDeviceMobileDuotone as AppIcon,
  PiFileTextDuotone as PageIcon,
  PiGlobeDuotone as WebsiteIcon,
  PiLightbulbDuotone as GuideIcon,
  PiLinkDuotone as LinkIcon,
  PiListBulletsDuotone as ListIcon,
  PiSparkleDuotone as OutlineIcon,
  PiStorefrontDuotone as PortalIcon,
  PiTextBDuotone as BoldIcon,
  PiTextHThreeDuotone as H3Icon,
  PiTextHTwoDuotone as H2Icon,
} from "react-icons/pi";
import FormFooter from "@core/components/form-footer";
import QuillEditor from "@core/ui/quill-editor";
import { PageHeader, ErrorSurface, Field, problemTitle, FormGroup, Form, WidgetCard, getStatusBadge } from "@steelyard/theme";
import { useConfirm } from "../../components/confirm-modal";
import { PagePreview, websiteSections } from "../../components/content/page-preview";
import { InfoBlock } from "../../components/detail-parts";
import { SectionTitle } from "../../components/section-title";
import { ApiError } from "../../lib/api";
import { formatMoment } from "../../lib/format";
import { formCard, formCardFooter, formSections } from "../../lib/form-layout";
import { useGoBack } from "../../lib/navigation";
import { PRIVACY_POLICY_OUTLINE } from "../../lib/privacy-policy-outline";
import { PRIVACY_POLICY_SLUG, getContentPage, publicAddresses, saveContentPage, slugify } from "../../lib/services/contentPages";

/** `/content/pages/new` (no slug) or `/content/pages/:slug`. The privacy policy may not exist yet and still opens. */
export async function pageEditorLoader({ params }: LoaderFunctionArgs) {
  const slug = params.slug ?? null;
  const page = slug ? await getContentPage(slug) : null;
  if (slug && !page && slug !== PRIVACY_POLICY_SLUG) throw new ApiError(404, { title: "No such page", status: 404 });
  return { slug, page };
}

// Headings, emphasis, lists and links: what a text page needs, and nothing the public pages have no
// style for (colour, font, alignment). Module-level: a new object each render would rebuild the editor.
const modules = {
  toolbar: [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]],
};

// The API's own limits and slug rule (ContentPage.cs, ContentPageService.cs). "new" is this screen's own address.
const pageSchema = z.object({
  title: z.string().trim().min(1, "Title is required").max(120, "Title must be 120 characters or fewer"),
  slug: z
    .string()
    .max(60, "The address must be 60 characters or fewer")
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "Use lower-case letters, numbers and single hyphens, e.g. terms-and-conditions")
    .refine((s) => s !== "new", "“new” is reserved; choose another address"),
  body: z.string(),
});
type PageFormValues = z.infer<typeof pageSchema>;
type FieldName = keyof PageFormValues;

export default function PageEditorPage() {
  const { slug, page: loaded } = useLoaderData() as Awaited<ReturnType<typeof pageEditorLoader>>;
  const [page, setPage] = useState(loaded);
  const [error, setError] = useState<unknown>(null);
  const [serverErrors, setServerErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [slugEdited, setSlugEdited] = useState(false);
  const navigate = useNavigate();
  const goBack = useGoBack("/content/pages");
  const confirm = useConfirm();

  const isPolicy = slug === PRIVACY_POLICY_SLUG;
  const heading = slug ? (page?.title ?? "Privacy policy") : "Add page";
  const defaultValues: PageFormValues = {
    title: loaded?.title ?? (isPolicy ? "Privacy policy" : ""),
    slug: slug ?? "",
    body: loaded?.body ?? "",
  };

  async function submit(values: PageFormValues) {
    setError(null);
    setServerErrors({});
    const target = slug ?? values.slug;
    try {
      // PUT creates or replaces, so a new page must not land on an address already in use.
      if (!slug && (await getContentPage(target))) {
        setServerErrors({ slug: "A page already has this address. Choose another, or edit that page from the list." });
        return;
      }
      const saved = await saveContentPage(target, { title: values.title.trim(), body: values.body });
      setPage(saved);
      toast.success(`“${saved.title}” published`);
      if (!slug) navigate(`/content/pages/${target}`, { replace: true });
    } catch (err) {
      setError(err);
      if (err instanceof ApiError) {
        const errors = (err.raw as { errors?: Record<string, string[]> } | null)?.errors ?? {};
        setServerErrors(Object.fromEntries(Object.entries(errors).map(([field, messages]) => [field, messages[0]])));
      }
      toast.error(problemTitle(err, "Could not publish this page"));
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={heading}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Content" }, { name: "Pages", href: "/content/pages" }, { name: heading }]}
      />

      <Form<PageFormValues> key={slug ?? "new"} validationSchema={pageSchema} useFormProps={{ defaultValues }} className={formCard} onSubmit={submit}>
        {({ register, control, watch, setValue, formState: { errors, isSubmitting } }) => {
          const title = watch("title");
          const body = watch("body");
          const addresses = publicAddresses(slug ?? (watch("slug") || "your-page"));
          const fillWithOutline = () => {
            const fill = () => setValue("body", PRIVACY_POLICY_OUTLINE, { shouldDirty: true });
            if (websiteSections(body).empty) return fill();
            confirm({
              title: "Replace what is in the editor?",
              consequence: "The editor's text is replaced by the suggested privacy policy. Nothing changes for customers until you click Publish.",
              confirmLabel: "Replace",
              onConfirm: fill,
            });
          };

          return (
            <>
              <div className={formSections}>
                {error ? <ErrorSurface error={error} action="publish this page" /> : null}

                <FormGroup title={<SectionTitle icon={PageIcon}>Page</SectionTitle>} description="Its title, and the address both sites show it at.">
                  <Field
                    label="Title"
                    required
                    maxLength={120}
                    autoComplete="off"
                    wrapperClassName="@xl:col-span-1 @5xl:col-span-2"
                    error={errors.title?.message ?? serverErrors.title}
                    {...register("title", {
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        if (!slug && !slugEdited) setValue("slug", slugify(event.target.value));
                      },
                    })}
                  />
                  {slug ? (
                    <InfoBlock icon={<LinkIcon aria-hidden className="size-4" />} tile="bg-gray-100 text-gray-700" label="Address">
                      <Text className="font-medium">{slug}</Text>
                      <Text className="text-xs text-gray-500">Set when the page was created; both sites link to it.</Text>
                    </InfoBlock>
                  ) : (
                    <Field
                      label="Address"
                      required
                      maxLength={60}
                      autoComplete="off"
                      wrapperClassName="@xl:col-span-1 @5xl:col-span-2"
                      hint="Filled in from the title. It cannot be changed after publishing, because both sites link to it."
                      error={errors.slug?.message ?? serverErrors.slug}
                      {...register("slug", { onChange: () => setSlugEdited(true) })}
                    />
                  )}
                </FormGroup>

                <FormGroup title={<SectionTitle icon={StatusIcon}>Where it shows</SectionTitle>} description="One text, published to every place a customer can read it.">
                  <InfoBlock icon={<StatusIcon aria-hidden className="size-4" />} tile="bg-green-lighter text-green-dark" label="Status">
                    {getStatusBadge(page ? "published" : "not_published")}
                  </InfoBlock>
                  <InfoBlock icon={<DateIcon aria-hidden className="size-4" />} tile="bg-blue-lighter text-blue-dark" label="Last published">
                    <Text className="font-medium">{page ? formatMoment(page.updatedAt) : "Never"}</Text>
                  </InfoBlock>
                  <InfoBlock icon={<PortalIcon aria-hidden className="size-4" />} tile="bg-orange-lighter text-orange-dark" label="Customer portal">
                    <Text className="break-all font-medium">{addresses.portal}</Text>
                    <Text className="text-xs text-gray-500">Listed in the footer, under Information</Text>
                  </InfoBlock>
                  <InfoBlock icon={<WebsiteIcon aria-hidden className="size-4" />} tile="bg-secondary-lighter text-secondary-dark" label="Website and apps">
                    <Text className="break-all font-medium">{addresses.website}</Text>
                    <Text className="text-xs text-gray-500">Listed in the website&apos;s footer; the apps read the same text</Text>
                  </InfoBlock>
                </FormGroup>

                <FormGroup title={<SectionTitle icon={PageIcon}>Content</SectionTitle>} description="Write on the left. The right shows it as customers will see it on each site.">
                  <div className="col-span-full grid gap-6 @5xl:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">
                    <div className="flex min-w-0 flex-col gap-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <Text className="text-sm text-gray-500">Heading 2 starts a section · Heading 3 a point inside it.</Text>
                        {isPolicy ? (
                          <Button type="button" variant="outline" size="sm" onClick={fillWithOutline}>
                            <OutlineIcon aria-hidden className="me-1.5 size-4" />
                            Use the suggested privacy policy
                          </Button>
                        ) : null}
                      </div>
                      <Controller
                        control={control}
                        name="body"
                        render={({ field: { onChange, value } }) => (
                          <QuillEditor
                            value={value}
                            onChange={onChange}
                            modules={modules}
                            error={serverErrors.body}
                            placeholder="Start with a short paragraph saying what this page is about, then one Heading 2 per section…"
                            className="[&>.ql-container_.ql-editor]:min-h-[560px]"
                          />
                        )}
                      />
                    </div>

                    <div className="flex min-w-0 flex-col gap-6">
                      <PagePreview
                        title={title.trim() || "Untitled page"}
                        eyebrow={isPolicy ? "Your details, kept simple" : "Infra Junction"}
                        html={body}
                      />
                      <WritingGuide isPolicy={isPolicy} />
                    </div>
                  </div>
                </FormGroup>
              </div>

              <FormFooter
                className={formCardFooter}
                submitBtnText={isSubmitting ? "Publishing…" : "Publish"}
                isLoading={isSubmitting}
                altBtnText="Cancel"
                handleAltBtn={goBack}
              />
            </>
          );
        }}
      </Form>
    </div>
  );
}

/** What each formatting choice turns into on the two public pages — the question a writer here actually has. */
function WritingGuide({ isPolicy }: { isPolicy: boolean }) {
  return (
    <WidgetCard title={<span className="inline-flex items-center gap-2"><GuideIcon aria-hidden className="size-5 text-primary" />How to write it</span>}>
      <ul className="mt-4 grid gap-4">
        <Tip icon={<H2Icon aria-hidden className="size-4" />} title="Heading 2 starts a section">
          The website numbers each one (01, 02, …) and lists it under “On this page”; the portal shows it as a section heading.
          {isPolicy
            ? " Suggested: What we collect · How we use it · Who we share it with · Cookies · How long we keep it · Your rights · Contact."
            : null}
        </Tip>
        <Tip icon={<H3Icon aria-hidden className="size-4" />} title="Heading 3 for a point inside a section">
          A short label for one point, with its paragraph underneath.
        </Tip>
        <Tip icon={<ListIcon aria-hidden className="size-4" />} title="Lists for things a reader checks off">
          Steps, conditions, what is and is not included.
        </Tip>
        <Tip icon={<BoldIcon aria-hidden className="size-4" />} title="Bold the names that matter">
          Company names, GSTINs, the services you mention.
        </Tip>
        <Tip icon={<LinkIcon aria-hidden className="size-4" />} title="Links: the full address">
          Paste the whole https:// address of any page you link to.
        </Tip>
        <Tip icon={<DateIcon aria-hidden className="size-4" />} title="Leave out the title and the date">
          Both sites already show the title above the text, and “Last updated” is set the moment you publish.
        </Tip>
        <Tip icon={<AppIcon aria-hidden className="size-4" />} title="Write for every reader">
          The same text reaches website visitors, shop customers and app users, so say “our website, shop and app” rather than
          one of them.
        </Tip>
      </ul>
    </WidgetCard>
  );
}

function Tip({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <li className="flex items-start gap-3">
      <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-lighter text-primary-dark">{icon}</span>
      <div className="min-w-0">
        <Text className="font-semibold text-gray-900">{title}</Text>
        <Text className="text-sm text-gray-500">{children}</Text>
      </div>
    </li>
  );
}
